import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import type { AppDispatch, RootState } from "../../../redux/store";

/* ⭐ this brand's produce comes off the same brand-scoped feed the
   grower's own FreshStore screen writes to. */
import {
  listenToBrandFoodFeed,
  stopBrandFoodFeed,
} from "../../../redux/foodSlice";
import { addToCart, decrementCartItem, clearCart } from "../../../redux/cartSlice";
import { listenToCommerceData } from "../../../redux/commercePartnersSlice";
import { fetchAllProductSummaries } from "../../../redux/productRatingSlice";
import { logout } from "../../../redux/authSlice";
import {
  listenToUserNotifications,
  stopUserNotificationsListener,
} from "../../../redux/notificationsSlice";
import { CurrencyContext } from "../../../GLOBAL/CurrencyContext";
import appLogo from "../../../assets/app/logo.png";

/* ⭐ CATEGORY SOURCE — the same collection FreshStore saves with.
   Scoped twice over: to Farm / Food / Grocery, and then to the nodes
   this one grower actually has live produce in. */
import {
  DEPARTMENTS,
  AllCategoriesFlat,
  type CategoryItem,
} from "../../../CATEGORIES/CategoriesCollection";
import { attributesForCategory } from "../../../CATEGORIES/ATTRIBUTES/CategoriesCollectionAttributes";

import {
  Search, ShoppingBag, X, ChevronRight, Plus, Minus,
  Heart, User, ShoppingCart, MapPin, ChevronDown, LogOut, ArrowLeft,
  Home, ShoppingBasket, Star, LayoutGrid, Check, Tag, Sparkles,
  Sprout, Leaf, WheatOff, Flame, Clock, Snowflake, Sun, Package,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   SCOPE — the three departments this marketplace covers, same
   set FreshStoreHome uses. Only the ones this grower actually
   stocks reach the oval.
═══════════════════════════════════════════════════════════ */
const FRESH_DEPT_IDS = ["07", "09", "13"]; // Farm · Food · Grocery
const FRESH_DEPARTMENTS: CategoryItem[] = DEPARTMENTS.filter((d) =>
  FRESH_DEPT_IDS.includes(d.id)
);

/* ═══════════════════════════════════════════════════════════
   HEARTLAS DESIGN TOKENS — same palette as FreshStoreHome
═══════════════════════════════════════════════════════════ */
const HEART = {
  bg: "#FBF6EE",
  panelBg: "#FFFFFF",

  dark: "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",

  red: "#C8202E",
  redDark: "#9E1521",
  redSoft: "#FBE7E9",

  gold: "#F2A93E",
  goldDark: "#D4900F",
  goldSoft: "#FCEFD4",

  green: "#3F8A4C",
  greenSoft: "#E7F3E8",

  border: "#EEDFC4",
  divider: "#F3E6CE",
};

const T = {
  pageBg: HEART.bg,
  cardBg: HEART.panelBg,
  black: HEART.dark,
  muted: HEART.textMuted,
  mutedDim: HEART.textFaint,
  red: HEART.red,
  redDim: HEART.redSoft,
  border: HEART.border,
  grad: `linear-gradient(135deg, ${HEART.red} 0%, ${HEART.redDark} 100%)`,
  gradH: `linear-gradient(90deg, ${HEART.red} 0%, ${HEART.redDark} 100%)`,
  outline: `1.5px solid ${HEART.border}`,
};

const GRAD_H = T.gradH;
const RGBA_RED = (a: number) => `rgba(200,32,46,${a})`;

/* accent comes straight from the grower's brand — no random tints */
const brandAccentColor = (partner: any): string =>
  partner?.brandColor || partner?.themeColor || partner?.color || HEART.red;

/* ═══════════════════════════════════════════════════════════
   CATEGORY TREE HELPERS — ancestry always resolved by walking
   parentId, never by string prefixes.
═══════════════════════════════════════════════════════════ */
const CAT_MAP = new Map<string, CategoryItem>();
AllCategoriesFlat.forEach((c) => CAT_MAP.set(c.id, c));

const childrenOf = (parentId: string | null) =>
  AllCategoriesFlat.filter((c) => c.parentId === parentId);

const chainCache = new Map<string, string[]>();
const chainIdsOf = (id?: string | number | null): string[] => {
  if (id === null || id === undefined || id === "") return [];
  const key = String(id);
  const hit = chainCache.get(key);
  if (hit) return hit;
  const chain: string[] = [];
  let cur = CAT_MAP.get(key);
  while (cur) {
    chain.unshift(cur.id);
    cur = cur.parentId ? CAT_MAP.get(cur.parentId) : undefined;
  }
  chainCache.set(key, chain);
  return chain;
};

const catName = (id?: string | number | null) =>
  id === null || id === undefined ? "" : CAT_MAP.get(String(id))?.name ?? "";

/** Full path, department included — there are three of them now. */
const catPathLabel = (id?: string | number | null) =>
  chainIdsOf(id).map(catName).join(" › ");

/** Does this category sit under Farm, Food or Grocery? */
const isFresh = (id?: string | number | null) =>
  chainIdsOf(id).some((cid) => FRESH_DEPT_IDS.includes(cid));

/* deterministic accent per department — keyed on the fixed list so a
   grower who only stocks Grocery still gets Grocery's colour */
const DEPT_ACCENTS = ["#7A9E3B", "#3F8A4C", "#D4900F"]; // Farm · Food · Grocery
const deptAccent = (deptId: string) => {
  const idx = FRESH_DEPARTMENTS.findIndex((d) => d.id === deptId);
  return DEPT_ACCENTS[idx >= 0 ? idx % DEPT_ACCENTS.length : 0];
};

/* ═══════════════════════════════════════════════════════════
   ATTRIBUTES — resolved the way the seller screen resolves them,
   then filtered to what the grower actually filled in. Nothing
   about the fields themselves lives in this file.
═══════════════════════════════════════════════════════════ */
const toAttributeCategoryId = (id?: string | number | null): string => {
  if (id === null || id === undefined || id === "") return "";
  const chain = chainIdsOf(id).map((cid) => CAT_MAP.get(cid)!).filter(Boolean);
  const anchor = [...chain].reverse().find((c) => c.level === "category" || c.level === "department");
  return anchor ? `0${anchor.id}` : "";
};

const attrCache = new Map<string, any[]>();
const resolveAttributes = (categoryId?: string | number | null): any[] => {
  if (categoryId === null || categoryId === undefined || categoryId === "") return [];
  const key = String(categoryId);
  const hit = attrCache.get(key);
  if (hit) return hit;

  const chain = [...chainIdsOf(categoryId)].reverse();
  const keys = [toAttributeCategoryId(categoryId), ...chain.flatMap((c) => [`0${c}`, c])].filter(Boolean);

  let found: any[] = [];
  for (const k of keys) {
    try {
      const defs = attributesForCategory(k as any);
      if (defs && defs.length) { found = defs; break; }
    } catch { /* unknown key — keep walking up */ }
  }
  attrCache.set(key, found);
  return found;
};

const resolveAttrValue = (attr: any, value: any): string => {
  if (value === undefined || value === null || value === "") return "";
  const label = (optId: any) => {
    const opt = attr.options?.find((o: any) => String(o.id) === String(optId));
    return opt ? (opt.name || opt.standard || String(optId)) : String(optId);
  };
  if (Array.isArray(value)) return value.map(label).join(", ");
  if (attr.options?.length) return label(value);
  return `${value}${attr.unit ? ` ${attr.unit}` : ""}`;
};

const isEmptyValue = (v: any) =>
  v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

interface SavedAttr { def: any; raw: any }
/** Only what the grower filled in. A blank attribute doesn't exist here. */
const savedAttrsOf = (item: any): SavedAttr[] => {
  const saved = item?.attributes ?? {};
  return resolveAttributes(item?.categoryId)
    .map((def: any) => ({ def, raw: saved[String(def.id)] }))
    .filter(({ raw }) => !isEmptyValue(raw));
};

interface Swatch { id: string; name: string; hex?: string }
interface ColorAttr { id: string; name: string; swatches: Swatch[] }
/** Saved colours only — empty unless the grower picked one. */
const savedColorsOf = (item: any): ColorAttr[] =>
  savedAttrsOf(item)
    .filter(({ def }) => def.inputType === "color")
    .map(({ def, raw }) => {
      const ids = Array.isArray(raw) ? raw : [raw];
      return {
        id: String(def.id),
        name: def.name,
        swatches: ids
          .map((optId: any) => def.options?.find((o: any) => String(o.id) === String(optId)))
          .filter(Boolean)
          .map((o: any) => ({ id: String(o.id), name: o.name || o.standard || String(o.id), hex: o.hex })),
      };
    })
    .filter((c) => c.swatches.length > 0);

/* ═══════════════════════════════════════════════════════════
   SIZES / EXTRAS — the grower's own definitions. New docs store
   portions as an array; older ones as a keyed map.
═══════════════════════════════════════════════════════════ */
interface Portion { id: string; name: string; price: number; unit?: string; stock?: number }
interface Extra { id: string; name: string; price: number }

const portionsOf = (item: any): Portion[] => {
  const raw = item?.portions;
  if (!raw) return [];
  const list: Portion[] = Array.isArray(raw)
    ? raw.map((p: any) => ({
        id: String(p?.id ?? p?.name ?? ""), name: p?.name ?? String(p?.id ?? ""),
        price: Number(p?.price) || 0, unit: p?.unit ?? "", stock: Number(p?.stock) || 0,
      }))
    : Object.entries(raw).map(([k, v]: [string, any]) => ({
        id: k, name: v?.name ?? k,
        price: Number(v?.price) || 0, unit: v?.unit ?? "", stock: Number(v?.stock) || 0,
      }));
  return list.filter((p) => p.id && p.name);
};

const extrasOf = (item: any): Extra[] =>
  Array.isArray(item?.extras)
    ? item.extras
        .map((e: any) => ({ id: String(e?.id ?? e?.name ?? ""), name: e?.name ?? "", price: Number(e?.price) || 0 }))
        .filter((e: Extra) => e.id && e.name)
    : [];

const buildCartId = (item: any, sizeId: string | null, extraIds: string[]) => {
  if (!sizeId && extraIds.length === 0) return item.listingId;
  const extraPart = extraIds.length ? extraIds.slice().sort().join("+") : "none";
  return `${item.listingId}__${sizeId ?? "base"}__${extraPart}`;
};

const computeItemPrice = (item: any, sizeId: string | null, extraIds: string[]) => {
  const base = item?.rate ?? 0;
  const size = portionsOf(item).find((p) => p.id === sizeId);
  const extras = extrasOf(item);
  const extrasSum = extraIds.reduce((sum, id) => sum + (extras.find((e) => e.id === id)?.price || 0), 0);
  return base + (size?.price || 0) + extrasSum;
};

const buildCartLabel = (item: any, sizeId: string | null, extraIds: string[]) => {
  const size = portionsOf(item).find((p) => p.id === sizeId);
  const extras = extrasOf(item);
  let label = item?.name || "";
  if (size) label += ` (${size.name}${size.unit ? ` · ${size.unit}` : ""})`;
  const names = extraIds.map((id) => extras.find((e) => e.id === id)?.name).filter(Boolean) as string[];
  if (names.length) label += ` + ${names.join(", ")}`;
  return label;
};

/* ═══════════════════════════════════════════════════════════
   FRESH HELPERS
═══════════════════════════════════════════════════════════ */
const measureLabel = (item: any) => {
  const n = item?.netContent, u = item?.unit;
  if (n && u) return `${n} ${u}`;
  if (u) return `per ${u}`;
  return "";
};

const daysLeft = (bestBefore?: string): number | null => {
  if (!bestBefore) return null;
  const t = Date.parse(bestBefore);
  if (Number.isNaN(t)) return null;
  return Math.ceil((t - Date.now()) / 86_400_000);
};

const freshChip = (item: any): { text: string; color: string } | null => {
  const d = daysLeft(item?.bestBefore);
  if (d === null) {
    const shelf = item?.shelfLifeDays;
    return shelf ? { text: `${shelf}-day shelf life`, color: HEART.green } : null;
  }
  if (d <= 0) return { text: "Use today", color: HEART.red };
  if (d === 1) return { text: "1 day left", color: HEART.red };
  if (d <= 3) return { text: `${d} days left`, color: HEART.goldDark };
  return { text: `Good for ${d} days`, color: HEART.green };
};

const STORAGE_META: Record<string, { label: string; icon: any }> = {
  frozen: { label: "Keep frozen", icon: Snowflake },
  chilled: { label: "Keep chilled", icon: Snowflake },
  ambient: { label: "Room temp", icon: Sun },
  dry: { label: "Keep dry", icon: Sun },
};

const SPICE = ["None", "Mild", "Medium", "Hot"];

const isOutOfStock = (item: any) => item?.available === false || (item?.stock ?? 0) <= 0;

type SortKey = "default" | "freshest" | "price_asc" | "price_desc" | "name_asc";

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600;700;800;900&family=Archivo+Black&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
::-webkit-scrollbar { display: none; }
input, button, select { font-family: inherit; }
input::placeholder { color: rgba(255,255,255,.55); }

@keyframes gradSpin { 0%{background-position:0% 50%;} 50%{background-position:100% 50%;} 100%{background-position:0% 50%;} }
@keyframes titleGlow { 0%,100%{ text-shadow: 0 2px 0 rgba(27,14,6,.08), 0 14px 30px rgba(200,32,46,.18);} 50%{ text-shadow: 0 2px 0 rgba(27,14,6,.08), 0 18px 40px rgba(200,32,46,.30);} }
@keyframes ovalSheen { 0%{ transform: translateX(-160%) skewX(-18deg);} 60%,100%{ transform: translateX(320%) skewX(-18deg);} }
@keyframes fsbSpin { to { transform: rotate(360deg); } }

.p-card { transition: box-shadow .22s, transform .22s; }
.p-card:hover { box-shadow: 0 4px 10px rgba(0,0,0,.06), 0 24px 48px rgba(0,0,0,.16); transform: translateY(-6px); }
.add-btn-3d { transition: transform .18s ease, box-shadow .18s ease; }
.add-btn-3d:hover { transform: translateY(-3px); box-shadow: 0 10px 24px var(--shadow) !important; }
.add-btn-3d:active { transform: translateY(0) scale(.96); box-shadow: 0 4px 14px var(--shadow) !important; }

.tab-item { display:flex; flex-direction:column; align-items:center; gap:3px; font-size:10px; font-weight:700; cursor:pointer; color:${T.mutedDim}; padding:4px 10px; transition:color .2s; }
.tab-item.active { color:${T.red}; }

.fsb-title {
  font-family: 'Archivo Black','Montserrat',sans-serif;
  font-weight: 900; text-transform: uppercase; letter-spacing: -0.02em; line-height: 0.95;
  background: linear-gradient(180deg, ${HEART.dark} 0%, ${HEART.dark} 60%, ${HEART.red} 130%);
  -webkit-background-clip: text; background-clip: text; color: transparent;
  animation: titleGlow 3.4s ease-in-out infinite;
}

.fsb-hbtn{display:flex;align-items:center;gap:6px;background:rgba(255,255,255,.16);
  border:1px solid rgba(255,255,255,.3);cursor:pointer;color:#fff;
  font-size:12px;font-weight:700;padding:9px 14px;border-radius:999px;
  flex-shrink:0;white-space:nowrap;transition:background .2s;font-family:inherit;}
.fsb-hbtn:hover{background:rgba(255,255,255,.26);}

/* ✨ THE OVAL — this grower's live food categories */
.fsb-oval {
  position: relative; display: flex; align-items: center; gap: 4px;
  padding: 7px 10px; border-radius: 999px;
  background:
    linear-gradient(#FFFDF8, #FFF6E6) padding-box,
    linear-gradient(120deg, ${HEART.gold}, ${HEART.red} 45%, ${HEART.green} 92%) border-box;
  border: 2px solid transparent;
  box-shadow: 0 10px 30px rgba(200,32,46,.14), 0 2px 6px rgba(27,14,6,.08), inset 0 1px 0 rgba(255,255,255,.9);
  overflow-x: auto; overflow-y: hidden; scrollbar-width: none; -webkit-overflow-scrolling: touch;
}
.fsb-oval::after {
  content: ""; position: absolute; top: 0; bottom: 0; left: 0; width: 90px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.75), transparent);
  animation: ovalSheen 5.5s ease-in-out infinite; pointer-events: none;
}
.fsb-oval-toggle {
  position: relative; flex-shrink: 0; display: flex; align-items: center; gap: 7px;
  border: none; cursor: pointer; border-radius: 999px; padding: 9px 16px;
  font-size: 12px; font-weight: 800; letter-spacing: .03em;
  color: ${HEART.textMuted}; background: transparent; white-space: nowrap;
  transition: color .18s, transform .18s;
}
.fsb-oval-toggle:hover { color: ${HEART.dark}; transform: translateY(-1px); }
.fsb-oval-toggle .fsb-ic { display: flex; font-size: 13px; transition: transform .18s; }
.fsb-oval-toggle:hover .fsb-ic { transform: rotate(-8deg) scale(1.12); }
.fsb-oval-toggle.active {
  color: #fff; background: linear-gradient(135deg, ${HEART.red}, ${HEART.redDark});
  box-shadow: 0 8px 18px rgba(200,32,46,.42), inset 0 1px 0 rgba(255,255,255,.28);
}
.fsb-oval-toggle.active .fsb-ic { transform: none; }

.fsb-tile{background:none;border:none;cursor:pointer;padding:0;border-radius:14px;overflow:hidden;}
.fsb-opt{transition:border-color .15s, background .15s;}
.fsb-opt:hover{border-color:${RGBA_RED(0.45)};}

.nav-item { display:flex; align-items:center; gap:12px; padding:13px 18px; cursor:pointer; font-size:14px; font-weight:700; color:${T.black}; background:none; width:100%; text-align:left; border:none; border-bottom:1px solid ${HEART.divider}; transition:background .15s; }
.nav-item:hover { background: ${HEART.goldSoft}; }

@media (prefers-reduced-motion: reduce){
  .fsb-oval::after,.fsb-title{animation:none;}
  .p-card,.add-btn-3d{transition:none;}
}
`;

const A: Record<string, React.CSSProperties> = {
  commerceCard: { borderRadius: 18, padding: 18, background: "#fff", marginBottom: 16, boxShadow: "0 2px 16px rgba(27,14,6,.06)", border: T.outline },
  commerceHeader: { fontSize: 16, fontWeight: 900, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", color: T.black },
  category: { padding: 10, borderRadius: 12, background: HEART.bg, marginTop: 8, border: `1px solid ${HEART.border}`, cursor: "pointer" },
  row: { display: "flex", alignItems: "center", gap: 10, fontWeight: 700, color: T.black, cursor: "pointer" },
  sub: { paddingLeft: 20, paddingTop: 8 },
  sidebar: { background: "rgba(255,255,255,.98)", borderRadius: 20, padding: 20, boxShadow: "0 4px 24px rgba(27,14,6,.08)", position: "sticky" as const, top: 116, maxHeight: "calc(100vh - 130px)", overflowY: "auto" as const, border: T.outline },
};

const S: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", background: T.pageBg, paddingBottom: 90, fontFamily: "'Montserrat',sans-serif", position: "relative" as const },
  content: { position: "relative" as const, zIndex: 1 },
  headerTop: { background: GRAD_H, backgroundSize: "200% 100%", position: "sticky", top: 0, zIndex: 200, boxShadow: `0 4px 24px ${RGBA_RED(0.25)}` },
  searchWrap: { display: "flex", alignItems: "center", flex: 1, background: "rgba(255,255,255,.18)", borderRadius: 999, overflow: "hidden", border: "1.5px solid rgba(255,255,255,.32)" },
  searchInput: { flex: 1, border: "none", outline: "none", padding: "10px 14px", fontSize: 13, color: "#fff", background: "transparent", fontFamily: "inherit" },
  searchBtn: { width: 42, minHeight: 40, background: "rgba(255,255,255,.16)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, borderRadius: "0 999px 999px 0" },
  desktopGrid: { display: "grid", gap: 24, padding: "24px 24px 0" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 20 },
  mobileGrid: { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12 },
  mobileTabs: { position: "fixed" as const, bottom: 0, left: 0, right: 0, height: 64, background: T.cardBg, display: "flex", justifyContent: "space-around", alignItems: "center", borderTop: `1px solid ${HEART.border}`, zIndex: 100 },
};

const PageWatermark: React.FC<{ src: string }> = ({ src }) => (
  <div aria-hidden="true" style={{
    position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
    backgroundImage: `url(${src})`, backgroundRepeat: "repeat",
    backgroundSize: "170px 170px", opacity: 0.045, filter: "grayscale(1)",
  }} />
);

/* ═══════════════════════════════════════════════════════════
   MAIN — one grower's fresh storefront
═══════════════════════════════════════════════════════════ */
const FreshStoreBrand: React.FC = () => {
  const { brandId } = useParams<{ brandId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { rate, symbol } = useContext(CurrencyContext);
  const cur = symbol ?? "€";
  const fx = (n: number) => (n ?? 0) * (rate ?? 1);
  const price = (n: number) => `${cur}${fx(n).toFixed(2)}`;

  const authUser = useSelector((s: RootState) => s.auth.user);
  const authLoaded = useSelector((s: RootState) => (s as any).auth?.authLoaded ?? true);
  const userId = authUser?.userId ?? "";

  /* brand-scoped produce feed */
  const brandFoodItems = useSelector((s: RootState) => (s as any).food?.brandFoodItems ?? []);
  const brandFoodLoading = useSelector((s: RootState) => (s as any).food?.brandFoodLoading ?? false);

  const cartItems = useSelector((s: RootState) => s.cart.items);
  const ratingSummaries = useSelector((s: RootState) => (s as any).productRatings?.summaries ?? {});
  const partner = useSelector((s: RootState) => (s as any).commercePartners?.currentPartner);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [catOpen, setCatOpen] = useState(false);
  const [desktopCatOpen, setDesktopCatOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSort, setActiveSort] = useState<SortKey>("default");
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  /* filter selections — department, then category, then anything deeper */
  const [activeDept, setActiveDept] = useState<string | null>(null);
  const [activeCat, setActiveCat] = useState<string | null>(null);
  const [activeDeep, setActiveDeep] = useState<string | null>(null);

  const [openDeptPanel, setOpenDeptPanel] = useState<string | null>(null);
  const [openNodes, setOpenNodes] = useState<Set<string>>(new Set());
  const [shopCatExpanded, setShopCatExpanded] = useState(true);
  const [wished, setWished] = useState<Set<string>>(new Set());

  /* detail sheet */
  const [detailItem, setDetailItem] = useState<any | null>(null);
  const [imgIndex, setImgIndex] = useState(0);
  const [qty, setQty] = useState(1);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedExtras, setSelectedExtras] = useState<string[]>([]);

  const cartTotal = cartItems.reduce((s: number, i: any) => s + (i.qty ?? 1), 0);

  /* ── effects ── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  useEffect(() => {
    if (!brandId) return;
    const unsub = dispatch(listenToCommerceData(brandId) as any);
    return () => { if (typeof unsub === "function") unsub(); };
  }, [brandId, dispatch]);

  useEffect(() => {
    if (!brandId) return;
    dispatch(listenToBrandFoodFeed(brandId) as any);
    dispatch(fetchAllProductSummaries() as any);
    return () => { dispatch(stopBrandFoodFeed() as any); };
  }, [brandId, dispatch]);

  useEffect(() => {
    if (!userId) return;
    dispatch(listenToUserNotifications(userId) as any);
    return () => { dispatch(stopUserNotificationsListener() as any); };
  }, [dispatch, userId]);

  useEffect(() => {
    if (!detailItem) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [detailItem]);

  /* only this grower's published produce, inside Farm / Food / Grocery */
  const liveProducts = useMemo(
    () => (brandFoodItems as any[]).filter((l: any) => l.isPublished !== false && isFresh(l.categoryId)),
    [brandFoodItems]
  );

  /* ═══════════════════════════════════════════════════════════
     LIVE CATEGORY DISCOVERY — every node that appears in the
     ancestry of live produce. This is what scopes the oval, the
     icon rail, the tree and the tiles to what THIS grower sells.
  ═══════════════════════════════════════════════════════════ */
  const { liveNodeIds, countByNode } = useMemo(() => {
    const nodes = new Set<string>();
    const count = new Map<string, number>();
    liveProducts.forEach((l: any) => {
      chainIdsOf(l.categoryId).forEach((cid) => {
        nodes.add(cid);
        count.set(cid, (count.get(cid) ?? 0) + 1);
      });
    });
    return { liveNodeIds: nodes, countByNode: count };
  }, [liveProducts]);

  /* the departments this grower actually stocks */
  const liveDepartments = useMemo(
    () => FRESH_DEPARTMENTS.filter((d) => liveNodeIds.has(d.id)),
    [liveNodeIds]
  );

  useEffect(() => {
    if (!liveDepartments.length) { setActiveDept(null); return; }
    setActiveDept((prev) => (prev && liveDepartments.some((c) => c.id === prev) ? prev : liveDepartments[0].id));
    setOpenDeptPanel((prev) => (prev && liveDepartments.some((c) => c.id === prev) ? prev : liveDepartments[0].id));
  }, [liveDepartments]);

  const dept = useMemo(
    () => liveDepartments.find((c) => c.id === activeDept) ?? liveDepartments[0] ?? null,
    [liveDepartments, activeDept]
  );

  const liveChildrenOf = (parentId: string | null) =>
    childrenOf(parentId).filter((c) => liveNodeIds.has(c.id));

  const deptCategories = useMemo(
    () => (dept ? liveChildrenOf(dept.id) : []),
    [dept, liveNodeIds] // eslint-disable-line
  );

  /* ── handlers ── */
  const closePanel = () => { setCatOpen(false); setDesktopCatOpen(false); };
  const resetAllFilters = () => { setActiveCat(null); setActiveDeep(null); setActiveSort("default"); setSearchQuery(""); };

  const handleDeptSelect = (id: string) => {
    setActiveDept(id); setActiveCat(null); setActiveDeep(null);
    setOpenDeptPanel(id); setOpenNodes(new Set());
  };
  const handleCatSelect = (id: string) => { setActiveCat((p) => (p === id ? null : id)); setActiveDeep(null); };
  const handleDeepSelect = (id: string) => setActiveDeep((p) => (p === id ? null : id));

  const toggleNode = (id: string) =>
    setOpenNodes((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const getQty = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty ?? 0;

  const SINGLE_BRAND_CART_MODE = true;
  const ensureCartBrandCompat = (newBrandId?: string) => {
    if (!SINGLE_BRAND_CART_MODE || !cartItems.length) return;
    const currentBrandId = cartItems[0]?.brandId;
    if (currentBrandId && newBrandId && currentBrandId !== newBrandId) dispatch(clearCart());
  };

  const addLine = (item: any, sizeId: string | null, extraIds: string[], count = 1) => {
    if (isOutOfStock(item)) return;
    ensureCartBrandCompat(item.brandId);
    const payload = {
      listingId: buildCartId(item, sizeId, extraIds),
      baseListingId: item.listingId,
      name: buildCartLabel(item, sizeId, extraIds),
      rate: computeItemPrice(item, sizeId, extraIds),
      photo: item.photos?.[0],
      hostId: item.hostId,
      brandId: item.brandId,
      unit: item.unit,
      netContent: item.netContent,
      portionId: sizeId ?? undefined,
      extraIds: extraIds.length ? extraIds : undefined,
    };
    for (let i = 0; i < Math.max(1, count); i++) dispatch(addToCart(payload as any));
  };

  const removeLine = (item: any, sizeId: string | null, extraIds: string[]) =>
    dispatch(decrementCartItem({ listingId: buildCartId(item, sizeId, extraIds) }));

  const openDetail = (item: any) => {
    setDetailItem(item); setImgIndex(0); setQty(1); setSelectedExtras([]);
    const sizes = portionsOf(item);
    setSelectedSize(sizes.length ? sizes[0].id : null);
  };
  const closeDetail = () => setDetailItem(null);

  /* anything configurable opens the sheet */
  const quickAdd = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (isOutOfStock(item)) return;
    if (portionsOf(item).length || extrasOf(item).length) { openDetail(item); return; }
    addLine(item, null, [], 1);
  };

  const toggleWish = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setWished((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const goToCheckout = () => navigate(`/Freshpay/${userId}`, { state: { items: cartItems } });

  const handleLogout = async () => {
    await dispatch(logout() as any);
    navigate("/AuthSwitch");
  };

  /* ═══════════════════════════════════
     FILTERING
  ═══════════════════════════════════ */
  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list: any[] = liveProducts.filter((l: any) => {
      if (isOutOfStock(l)) return false;
      const chain = chainIdsOf(l.categoryId);
      if (activeDept && !chain.includes(activeDept)) return false;
      if (activeCat && !chain.includes(activeCat)) return false;
      if (activeDeep && !chain.includes(activeDeep)) return false;
      if (q) {
        const hay = `${l.name ?? ""} ${l.description ?? ""} ${l.category ?? ""} ${l.unit ?? ""} ${(l.allergens ?? []).join(" ")} ${(l.tags ?? []).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    if (activeSort === "price_asc") list = [...list].sort((a, b) => (a.rate ?? 0) - (b.rate ?? 0));
    else if (activeSort === "price_desc") list = [...list].sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0));
    else if (activeSort === "name_asc") list = [...list].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    else if (activeSort === "freshest")
      list = [...list].sort((a, b) => {
        const av = Date.parse(a.harvestedOn ?? a.bestBefore ?? "") || 0;
        const bv = Date.parse(b.harvestedOn ?? b.bestBefore ?? "") || 0;
        return bv - av;
      });

    return list;
  }, [liveProducts, activeDept, activeCat, activeDeep, searchQuery, activeSort]);

  const activeFilterName = activeDeep ? catName(activeDeep) : activeCat ? catName(activeCat) : null;

  /* grower info */
  const brandAccent = brandAccentColor(partner);
  const shopName = partner?.name || "Grower";
  const shopLogo = partner?.image || appLogo;
  const shopCity = partner?.address?.city || null;
  const shopCountry = partner?.address?.country || null;

  /* ═══════════════════════════════════
     HEADER  (plain function calls, so the search input keeps focus)
  ═══════════════════════════════════ */
  const headerRow = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: isMobile ? "12px 14px" : "14px 20px" }}>
      <button className="fsb-hbtn" onClick={() => navigate(-1)} style={{ padding: "9px 11px" }} aria-label="Back">
        <ArrowLeft size={15} />
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        <div style={{ background: "#fff", borderRadius: "50%", padding: 2, display: "inline-flex" }}>
          <img src={shopLogo} alt={shopName}
            style={{ width: 30, height: 30, borderRadius: "50%", objectFit: "cover", display: "block" }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = appLogo; }} />
        </div>
        {!isMobile && (
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
            <span style={{ fontSize: 14, fontWeight: 800, color: "#fff" }}>{shopName}</span>
            {(shopCity || shopCountry) && (
              <span style={{ fontSize: 10, color: "rgba(255,255,255,.85)", display: "flex", alignItems: "center", gap: 3 }}>
                <MapPin size={9} color="rgba(255,255,255,.85)" />
                {[shopCity, shopCountry].filter(Boolean).join(", ")}
              </span>
            )}
          </div>
        )}
      </div>

      <div style={S.searchWrap}>
        <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={`Search ${shopName}…`} style={S.searchInput} aria-label="Search this grower" />
        {searchQuery && (
          <button onClick={() => setSearchQuery("")} aria-label="Clear search"
            style={{ background: "none", border: "none", cursor: "pointer", padding: "0 8px", color: "#fff" }}>
            <X size={13} />
          </button>
        )}
        <button style={S.searchBtn} aria-label="Search"><Search size={16} color="#fff" /></button>
      </div>

      {liveDepartments.length > 0 && (
        <button onClick={() => (isMobile ? setCatOpen(true) : setDesktopCatOpen((v) => !v))} className="fsb-hbtn">
          <LayoutGrid size={14} />
          <span style={{ display: isMobile ? "none" : "inline" }}>Categories</span>
        </button>
      )}

      <button onClick={goToCheckout} className="fsb-hbtn" style={{ position: "relative" }}>
        <ShoppingCart size={16} />
        {!isMobile && <span>{cartTotal > 0 ? `${cartTotal} items` : "Basket"}</span>}
        {cartTotal > 0 && (
          <span style={{ position: "absolute", top: -6, right: -6, background: HEART.redDark, color: "#fff", fontSize: 9, fontWeight: 900, width: 17, height: 17, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {cartTotal > 99 ? "99+" : cartTotal}
          </span>
        )}
      </button>

      {!isMobile && (
        <div style={{ position: "relative" }}>
          <button onClick={() => setProfileMenuOpen((v) => !v)} className="fsb-hbtn" aria-label="Profile"><User size={15} /></button>
          <AnimatePresence>
            {profileMenuOpen && (
              <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", width: 200, background: "#fff", borderRadius: 16, border: T.outline, boxShadow: "0 16px 40px rgba(27,14,6,.2)", overflow: "hidden", zIndex: 400 }}>
                <button className="nav-item" onClick={() => navigate(`/profile/${userId}`)}><User size={15} /> Profile</button>
                <button className="nav-item" onClick={() => navigate(`/FreshOrders/${userId}`)}><ShoppingBag size={15} /> Orders</button>
                <button className="nav-item" style={{ color: T.red, borderBottom: "none" }} onClick={handleLogout}><LogOut size={15} /> Sign out</button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );

  /* ═══════════════════════════════════
     TITLE + OVAL
  ═══════════════════════════════════ */
  const titleBanner = () => (
    <div style={{ padding: isMobile ? "6px 4px 4px" : "4px 4px 8px", display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
      <span className="fsb-title" style={{ fontSize: isMobile ? 30 : 46 }}>{dept?.name ?? shopName}</span>
      <span style={{ fontSize: isMobile ? 11 : 13, fontWeight: 800, letterSpacing: "0.16em", color: T.mutedDim, textTransform: "uppercase" }}>
        {shopCity ? `${shopName} · ${shopCity}` : shopName}
      </span>
    </div>
  );

  const ovalBar = () => {
    if (liveDepartments.length <= 1) return null;
    return (
      <div style={{ padding: isMobile ? "0 4px 14px" : "0 4px 18px" }}>
        <div className="fsb-oval">
          <span style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0, padding: "0 6px 0 8px", color: HEART.green }}>
            <Sparkles size={14} />
          </span>
          {liveDepartments.map((c) => {
            const isActive = c.id === activeDept;
            return (
              <button key={c.id} className={`fsb-oval-toggle${isActive ? " active" : ""}`} onClick={() => handleDeptSelect(c.id)}>
                <span className="fsb-ic" style={{ color: isActive ? "#fff" : deptAccent(c.id) }}>{c.icon}</span>
                {c.name}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════
     SIDEBAR TREE — brand-scoped, with live counts
  ═══════════════════════════════════ */
  const TreeNode: React.FC<{ node: CategoryItem; depth: number }> = ({ node, depth }) => {
    const kids = liveChildrenOf(node.id);
    const isOpen = openNodes.has(node.id);
    const isPicked = activeCat === node.id || activeDeep === node.id;
    const accent = dept ? deptAccent(dept.id) : T.red;

    return (
      <div style={depth === 0 ? A.category : { paddingLeft: 14, paddingTop: 7 }}>
        <div style={{ ...A.row, fontSize: depth === 0 ? 14 : 13, color: isPicked ? T.red : T.black }}
          onClick={() => {
            if (depth === 0) handleCatSelect(node.id);
            else handleDeepSelect(node.id);
            if (kids.length) toggleNode(node.id);
            else closePanel();
          }}>
          <span style={{ display: "flex", color: isPicked ? T.red : accent }}>{node.icon}</span>
          {node.name}
          <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: T.mutedDim }}>{countByNode.get(node.id) ?? 0}</span>
          {kids.length > 0 && (
            <motion.div animate={{ rotate: isOpen ? 90 : 0 }} style={{ marginLeft: "auto" }}>
              <ChevronRight size={13} color={T.mutedDim} />
            </motion.div>
          )}
          {isPicked && kids.length === 0 && <Check size={13} color={T.red} style={{ marginLeft: "auto" }} />}
        </div>
        <AnimatePresence>
          {isOpen && kids.length > 0 && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
              <div style={A.sub}>{kids.map((k) => <TreeNode key={k.id} node={k} depth={depth + 1} />)}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const deptTree = () => {
    if (!dept) return null;
    const isOpen = openDeptPanel === dept.id;
    return (
      <div style={A.commerceCard}>
        <div style={A.commerceHeader} onClick={() => setOpenDeptPanel(isOpen ? null : dept.id)}>
          {dept.name}
          <motion.div animate={{ rotate: isOpen ? 90 : 0 }}><ChevronRight size={16} /></motion.div>
        </div>
        <AnimatePresence>
          {isOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
              {deptCategories.length === 0
                ? <p style={{ fontSize: 12, color: T.mutedDim, paddingTop: 10 }}>Nothing deeper in this category yet.</p>
                : deptCategories.map((c) => <TreeNode key={c.id} node={c} depth={0} />)}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const mobileCategorySheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCatOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,.55)", zIndex: 400 }} />
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
            style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "82vh", borderRadius: "22px 22px 0 0", background: "#fff", zIndex: 401, border: T.outline, overflowY: "auto" }}>
            <div style={{ width: 36, height: 4, background: HEART.border, borderRadius: 2, margin: "14px auto 0" }} />
            <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${HEART.border}`, padding: "12px 20px 10px" }}>
              <span style={{ fontWeight: 700, fontSize: 11, letterSpacing: "0.15em", color: T.red }}>CATEGORIES</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setCatOpen(false)} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer" }}>
                <X size={15} color={T.muted} />
              </button>
            </div>
            <div style={{ padding: "16px 16px 32px" }}>{ovalBar()}{deptTree()}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ═══════════════════════════════════
     ICON RAIL — live sub-categories + All
  ═══════════════════════════════════ */
  const categoryIconBar = () => {
    if (!deptCategories.length || !dept) return null;
    const accent = deptAccent(dept.id);
    return (
      <div style={{ marginBottom: 22, padding: isMobile ? "14px 16px" : "18px 24px", background: HEART.panelBg, borderRadius: 18, border: T.outline }}>
        <div style={{ display: "flex", gap: isMobile ? 14 : 22, overflowX: "auto", scrollbarWidth: "none" as any, WebkitOverflowScrolling: "touch" as any, paddingBottom: 2 }}>
          <button onClick={() => { setActiveCat(null); setActiveDeep(null); }}
            style={{ background: "none", border: "none", cursor: "pointer", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "2px 4px" }}>
            <div style={{
              width: isMobile ? 46 : 54, height: isMobile ? 46 : 54, borderRadius: "50%",
              background: activeCat === null ? GRAD_H : "#fff",
              border: `2px solid ${activeCat === null ? T.red : HEART.border}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: activeCat === null ? `0 8px 20px ${RGBA_RED(0.3)}` : "none",
            }}>
              <ShoppingBasket size={isMobile ? 20 : 24} color={activeCat === null ? "#fff" : accent} />
            </div>
            <span style={{ fontSize: 10, fontWeight: activeCat === null ? 800 : 600, color: activeCat === null ? T.red : T.black }}>All</span>
          </button>

          {deptCategories.map((node) => {
            const isSel = activeCat === node.id;
            const n = countByNode.get(node.id) ?? 0;
            return (
              <button key={node.id} onClick={() => handleCatSelect(node.id)}
                style={{ background: "none", border: "none", cursor: "pointer", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "2px 4px", position: "relative" }}>
                <div style={{
                  width: isMobile ? 46 : 54, height: isMobile ? 46 : 54, borderRadius: "50%", overflow: "hidden",
                  border: `2px solid ${isSel ? T.red : HEART.border}`,
                  boxShadow: isSel ? `0 0 0 3px ${T.redDim}` : "none", transition: "all .2s",
                }}>
                  {(node as any).image ? (
                    <img src={(node as any).image} alt={node.name} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: isSel ? 1 : 0.85 }} />
                  ) : (
                    <div style={{ width: "100%", height: "100%", background: accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ color: "#fff", display: "flex", fontSize: isMobile ? 18 : 22 }}>{node.icon}</span>
                    </div>
                  )}
                </div>
                {n > 1 && (
                  <span style={{ position: "absolute", top: -2, right: -2, minWidth: 16, height: 16, padding: "0 4px", borderRadius: 999, background: T.red, color: "#fff", fontSize: 9, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid #fff" }}>
                    {n > 99 ? "99+" : n}
                  </span>
                )}
                <span style={{ fontSize: 10, fontWeight: isSel ? 800 : 600, color: isSel ? T.red : T.black, whiteSpace: "nowrap" }}>{node.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════
     SHOP BY CATEGORY — live tiles, filter not nav
  ═══════════════════════════════════ */
  const shopByCategory = () => {
    if (!deptCategories.length || !dept) return null;
    const focusId = activeCat ?? deptCategories[0]?.id ?? dept.id;
    const tiles = liveChildrenOf(focusId);
    if (!tiles.length) return null;

    const chunks: CategoryItem[][] = [];
    for (let i = 0; i < tiles.length; i += 4) chunks.push(tiles.slice(i, i + 4));
    const focusName = focusId === dept.id ? dept.name : catName(focusId);
    const groups = chunks.map((t, idx) => ({ title: chunks.length > 1 ? `${focusName} ${idx + 1}` : (focusName || "All"), tiles: t }));

    return (
      <div style={{ marginBottom: 28 }}>
        <div onClick={() => setShopCatExpanded((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none", marginBottom: 14 }}>
          <span style={{ fontWeight: 900, fontSize: isMobile ? 16 : 18, color: T.black, letterSpacing: -0.3 }}>Shop by category</span>
          <motion.div animate={{ rotate: shopCatExpanded ? 0 : -90 }} transition={{ duration: 0.2 }}>
            <ChevronDown size={20} color={T.muted} />
          </motion.div>
        </div>

        <AnimatePresence initial={false}>
          {shopCatExpanded && (
            <motion.div key={`${dept.id}-${focusId}`} initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
              <div style={isMobile
                ? { display: "flex", gap: 14, overflowX: "auto", scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" as any, scrollbarWidth: "none" as any, paddingBottom: 4 }
                : { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 18 }}>
                {groups.map((group, gi) => (
                  <div key={`${group.title}-${gi}`}
                    style={{ flexShrink: 0, ...(isMobile ? { width: "78vw", scrollSnapAlign: "start" } : {}), background: "#fff", borderRadius: 20, overflow: "hidden", border: T.outline, boxShadow: "0 6px 28px rgba(27,14,6,.06)", transition: "box-shadow .22s, border-color .22s, transform .22s" }}
                    onMouseEnter={(e) => { const el = e.currentTarget as HTMLElement; el.style.boxShadow = `0 8px 0 ${T.redDim}, 0 16px 38px rgba(27,14,6,.16)`; el.style.borderColor = T.red; el.style.transform = "translateY(-4px)"; }}
                    onMouseLeave={(e) => { const el = e.currentTarget as HTMLElement; el.style.boxShadow = "0 6px 28px rgba(27,14,6,.06)"; el.style.borderColor = HEART.border; el.style.transform = "translateY(0)"; }}>
                    <div style={{ padding: isMobile ? "10px 12px 6px" : "14px 16px 8px", fontWeight: 900, fontSize: isMobile ? 12 : 14, color: T.black, letterSpacing: -0.2 }}>{group.title}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: isMobile ? 4 : 8, padding: isMobile ? "0 8px 8px" : "0 10px 10px" }}>
                      {group.tiles.map((node) => {
                        const sel = activeDeep === node.id;
                        const hasKids = liveChildrenOf(node.id).length > 0;
                        return (
                          <button key={node.id} className="fsb-tile" onClick={() => handleDeepSelect(node.id)}>
                            <div style={{ width: "100%", aspectRatio: "1/1", borderRadius: isMobile ? 10 : 14, overflow: "hidden", position: "relative", background: HEART.bg, outline: sel ? `2.5px solid ${T.red}` : "none", outlineOffset: 2, transition: "outline .15s" }}>
                              {(node as any).image ? (
                                <>
                                  <img src={(node as any).image} alt={node.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                  <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top, ${HEART.dark}EE, transparent 55%)` }} />
                                  <span style={{ position: "absolute", bottom: isMobile ? 5 : 7, left: 4, right: 4, textAlign: "center", fontSize: isMobile ? 9 : 11, fontWeight: 800, color: "#fff", lineHeight: 1.15, textShadow: "0 1px 4px rgba(0,0,0,.5)" }}>{node.name}</span>
                                </>
                              ) : (
                                <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, padding: 4 }}>
                                  <span style={{ fontSize: isMobile ? 22 : 26, color: dept ? deptAccent(dept.id) : T.red, display: "flex" }}>{node.icon}</span>
                                  <span style={{ fontSize: isMobile ? 9 : 10.5, fontWeight: 800, color: T.black, textAlign: "center", lineHeight: 1.15 }}>{node.name}</span>
                                </div>
                              )}
                              {sel ? (
                                <div style={{ position: "absolute", top: 5, right: 5, width: 18, height: 18, borderRadius: "50%", background: T.red, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <Check size={11} color="#fff" strokeWidth={3} />
                                </div>
                              ) : hasKids ? (
                                <div style={{ position: "absolute", top: 5, right: 5, width: 16, height: 16, borderRadius: "50%", background: "rgba(255,255,255,.9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <ChevronRight size={10} color={T.red} />
                                </div>
                              ) : null}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  /* ═══════════════════════════════════
     PRODUCE CARD — floating crop with fresh signals
  ═══════════════════════════════════ */
  const produceCard = (item: any, isMob: boolean) => {
    const configurable = portionsOf(item).length > 0 || extrasOf(item).length > 0;
    const q = configurable ? 0 : getQty(item.listingId);
    const summary = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;
    const discount = item.compareAtPrice && item.compareAtPrice > (item.rate ?? 0)
      ? Math.round(((item.compareAtPrice - (item.rate ?? 0)) / item.compareAtPrice) * 100) : null;
    const fresh = freshChip(item);
    const measure = measureLabel(item);
    const isWished = wished.has(item.listingId);
    const accent = brandAccent;

    const overlap = isMob ? 58 : 100;
    const imgZoneH = isMob ? 200 : 300;

    return (
      <motion.div key={item.listingId} className="p-card" whileTap={{ scale: 0.96 }}
        style={{ borderRadius: 20, overflow: "visible", background: "#fff", boxShadow: "0 1px 2px rgba(27,14,6,.05), 0 10px 28px rgba(27,14,6,.10)", cursor: "pointer", display: "flex", flexDirection: "column", height: "100%", marginTop: overlap }}
        onClick={() => openDetail(item)}>
        <div style={{ width: "100%", height: imgZoneH, marginTop: -overlap, position: "relative", zIndex: 4, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {item.photos?.[0] ? (
            <div style={{ width: "115%", height: "115%", transform: "translateY(-5%)" }}>
              <motion.img src={item.photos[0]} alt={item.name} whileHover={{ scale: 1.06 }} transition={{ type: "spring", stiffness: 300, damping: 24 }}
                style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "bottom", display: "block", filter: "drop-shadow(0 16px 20px rgba(27,14,6,.25))" }}
                onError={(e) => { (e.currentTarget as HTMLImageElement).src = appLogo; }} />
            </div>
          ) : (
            <div style={{ width: "60%", height: "70%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Sprout size={isMob ? 40 : 50} color={HEART.green} style={{ opacity: 0.35 }} />
            </div>
          )}

          <button onClick={(e) => toggleWish(item.listingId, e)} aria-label="Save"
            style={{ position: "absolute", top: overlap - 4, right: 4, background: "rgba(255,255,255,.92)", border: "none", width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 8px rgba(27,14,6,.12)", zIndex: 6 }}>
            <Heart size={13} color={isWished ? T.red : T.muted} fill={isWished ? T.red : "none"} />
          </button>

          {discount && (
            <div style={{ position: "absolute", top: overlap + 8, left: 4, background: T.red, color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 999, zIndex: 5 }}>{discount}% OFF</div>
          )}
          {item.isOrganic && (
            <div style={{ position: "absolute", top: overlap + (discount ? 30 : 8), left: 4, zIndex: 6, display: "flex", alignItems: "center", gap: 3, background: HEART.greenSoft, color: HEART.green, fontSize: 9, fontWeight: 800, padding: "3px 8px", borderRadius: 999 }}>
              <Leaf size={9} /> Organic
            </div>
          )}
          {hasRating && (
            <div style={{ position: "absolute", bottom: 2, left: 4, zIndex: 6, background: "rgba(255,255,255,.92)", border: `1px solid ${T.border}`, borderRadius: 999, padding: isMob ? "3px 7px" : "4px 9px", display: "flex", alignItems: "center", gap: 3 }}>
              <Star size={isMob ? 9 : 10} fill={T.black} color={T.black} />
              <span style={{ color: T.black, fontWeight: 800, fontSize: isMob ? 9 : 10 }}>{summary.avgRating.toFixed(1)}</span>
            </div>
          )}
        </div>

        <div style={{ padding: isMob ? "2px 12px 12px" : "0 16px 16px", display: "flex", flexDirection: "column", gap: isMob ? 2 : 3, flex: 1, justifyContent: "flex-end" }}>
          <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: T.mutedDim, display: "flex", alignItems: "center", gap: 4 }}>
            <Tag size={9} /> {measure || catName(item.categoryId) || "produce"}
          </span>
          <div style={{ fontWeight: 800, fontSize: isMob ? 13 : 15, color: T.black, lineHeight: 1.25, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", minHeight: isMob ? 19 : 24 }}>
            {item.name}
          </div>

          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 900, fontSize: isMob ? 16 : 19, color: T.black }}>
              {configurable ? "From " : ""}{price(item.rate ?? 0)}
            </span>
            {discount && (
              <span style={{ fontSize: isMob ? 11 : 12, color: T.mutedDim, textDecoration: "line-through" }}>{price(item.compareAtPrice ?? 0)}</span>
            )}
          </div>
          {fresh && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 9.5, fontWeight: 800, color: fresh.color }}>
              <Clock size={9} /> {fresh.text}
            </span>
          )}

          {q > 0 ? (
            <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={(e) => e.stopPropagation()} className="add-btn-3d"
              style={{ width: "100%", height: isMob ? 40 : 46, boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fff", border: `2px solid ${accent}`, borderRadius: 999, padding: "0 6px", ["--shadow" as any]: `${accent}45`, boxShadow: "0 4px 14px var(--shadow)", marginTop: isMob ? 2 : 4 }}>
              <button aria-label="Remove one" style={{ width: isMob ? 26 : 30, height: isMob ? 26 : 30, borderRadius: "50%", border: "none", background: accent, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                onClick={(e) => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }}>
                <Minus size={isMob ? 12 : 14} color="#fff" />
              </button>
              <span style={{ color: accent, fontWeight: 900, fontSize: isMob ? 13 : 15 }}>{q}</span>
              <button aria-label="Add one" style={{ width: isMob ? 26 : 30, height: isMob ? 26 : 30, borderRadius: "50%", border: "none", background: accent, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                onClick={(e) => quickAdd(item, e)}>
                <Plus size={isMob ? 12 : 14} color="#fff" />
              </button>
            </motion.div>
          ) : (
            <motion.button className="add-btn-3d" whileTap={{ scale: 0.96 }}
              style={{ width: "100%", height: isMob ? 40 : 46, boxSizing: "border-box", borderRadius: 999, marginTop: isMob ? 2 : 4, border: `2px solid ${accent}`, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: "#fff", ["--shadow" as any]: `${accent}45`, boxShadow: "0 4px 14px var(--shadow)", cursor: "pointer" }}
              onClick={(e) => quickAdd(item, e)}>
              <ShoppingCart size={isMob ? 13 : 15} color={accent} />
              <span style={{ color: accent, fontWeight: 900, fontSize: isMob ? 11 : 13, letterSpacing: "0.06em" }}>
                {configurable ? "CHOOSE" : "ADD TO BASKET"}
              </span>
            </motion.button>
          )}
        </div>
      </motion.div>
    );
  };

  const emptyState = (title: string, body: string) => (
    <div style={{ textAlign: "center", padding: 56 }}>
      <div style={{ fontSize: 48, marginBottom: 14 }}>🥬</div>
      <h3 style={{ fontWeight: 900, fontSize: 22, color: T.black, marginBottom: 10 }}>{title}</h3>
      <p style={{ color: T.muted, marginBottom: 24, fontSize: 13 }}>{body}</p>
      {(searchQuery || activeCat || activeDeep) && (
        <button onClick={resetAllFilters} style={{ padding: "12px 28px", borderRadius: 999, border: "none", background: GRAD_H, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>
          Clear filters
        </button>
      )}
    </div>
  );

  const renderProducts = (isMob: boolean) => (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 4, height: 22, borderRadius: 2, background: GRAD_H }} />
          <span style={{ fontWeight: 900, fontSize: 15, color: T.black }}>
            {activeFilterName ?? (dept?.name ? `All ${dept.name}` : "Everything")}
          </span>
          <span style={{ fontSize: 12, color: T.muted, background: HEART.goldSoft, padding: "2px 9px", borderRadius: 999 }}>{filteredProducts.length}</span>
        </div>
        <select value={activeSort} onChange={(e) => setActiveSort(e.target.value as SortKey)} aria-label="Sort produce"
          style={{ border: T.outline, borderRadius: 999, padding: "5px 12px", fontSize: 11, fontWeight: 700, color: T.muted, background: "#fff", cursor: "pointer", outline: "none" }}>
          <option value="default">Featured</option>
          <option value="freshest">Freshest first</option>
          <option value="price_asc">Price: low → high</option>
          <option value="price_desc">Price: high → low</option>
          <option value="name_asc">Name A–Z</option>
        </select>
      </div>

      {brandFoodLoading && !liveProducts.length ? (
        <div style={{ textAlign: "center", padding: 56 }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", border: `3px solid ${T.redDim}`, borderTopColor: T.red, animation: "fsbSpin .8s linear infinite", margin: "0 auto 14px" }} />
          <p style={{ color: T.muted, fontSize: 13 }}>Loading the stall…</p>
        </div>
      ) : filteredProducts.length === 0 ? (
        liveProducts.length === 0
          ? emptyState("Nothing on the stall yet", "This grower hasn't listed any produce yet — check back soon.")
          : emptyState(searchQuery ? "No matches" : "Nothing here right now", searchQuery ? `Nothing matches “${searchQuery}”.` : "Try another department or category from this grower.")
      ) : (
        <div style={isMob ? S.mobileGrid : S.grid}>
          {filteredProducts.map((item: any) => produceCard(item, isMob))}
        </div>
      )}
    </div>
  );

  /* ═══════════════════════════════════
     DETAIL — sizes, extras, and only what the grower saved
  ═══════════════════════════════════ */
  const detailModal = () => {
    if (!detailItem) return null;
    const item = detailItem;

    const photos: string[] = item.photos?.length ? item.photos : [];
    const summary = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;

    const sizes = portionsOf(item);
    const extras = extrasOf(item);
    const savedAttrs = savedAttrsOf(item);
    const colorAttrs = savedColorsOf(item);
    const detailRows = savedAttrs
      .filter(({ def }) => def.inputType !== "color")
      .map(({ def, raw }) => ({ name: def.name, value: resolveAttrValue(def, raw) }))
      .filter((r) => r.value);

    const stock = item.stock ?? 0;
    const soldOut = isOutOfStock(item);
    const cartId = buildCartId(item, selectedSize, selectedExtras);
    const inCart = cartItems.find((i: any) => i.listingId === cartId)?.qty ?? 0;
    const unitPrice = fx(computeItemPrice(item, selectedSize, selectedExtras));
    const lineTotal = `${cur}${(unitPrice * qty).toFixed(2)}`;

    const fresh = freshChip(item);
    const measure = measureLabel(item);
    const storage = STORAGE_META[String(item.storage ?? "").toLowerCase()];
    const StorageIcon = storage?.icon ?? Package;
    const allergens: string[] = Array.isArray(item.allergens) ? item.allergens : [];
    const discount = item.compareAtPrice && item.compareAtPrice > (item.rate ?? 0)
      ? Math.round(((item.compareAtPrice - (item.rate ?? 0)) / item.compareAtPrice) * 100) : null;

    const needsSize = sizes.length > 0 && !selectedSize;
    const canAdd = !soldOut && !needsSize;

    const closeBtn = (
      <button onClick={closeDetail} aria-label="Close"
        style={{ background: "rgba(255,255,255,.92)", border: "none", width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 8px rgba(27,14,6,.18)" }}>
        <X size={15} color={T.black} />
      </button>
    );

    const chip = (key: string, bg: string, color: string, icon: React.ReactNode, text: string) => (
      <span key={key} style={{ display: "flex", alignItems: "center", gap: 4, background: bg, color, fontSize: 11, fontWeight: 800, padding: "5px 10px", borderRadius: 999 }}>
        {icon} {text}
      </span>
    );

    const content = (
      <div style={{ background: "#fff", borderRadius: isMobile ? "24px 24px 0 0" : 20, overflow: "hidden", maxWidth: isMobile ? "100%" : 470, width: "100%", maxHeight: isMobile ? "88vh" : "84vh", display: "flex", flexDirection: "column" }}>
        <div style={{ height: 4, background: GRAD_H, backgroundSize: "200% 100%", animation: "gradSpin 4s ease infinite", flexShrink: 0 }} />
        {isMobile && <div style={{ width: 36, height: 4, borderRadius: 2, background: HEART.border, margin: "12px auto 0", flexShrink: 0 }} />}

        <div style={{ overflowY: "auto", padding: "18px 20px 0" }}>
          {photos.length > 0 ? (
            <div style={{ position: "relative", width: "100%", aspectRatio: "16/10", borderRadius: 14, overflow: "hidden", marginBottom: 16, background: HEART.bg }}>
              <img src={photos[imgIndex]} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              {discount && <div style={{ position: "absolute", top: 10, left: 10, background: T.red, color: "#fff", fontSize: 10, fontWeight: 800, padding: "3px 10px", borderRadius: 999 }}>{discount}% OFF</div>}
              <div style={{ position: "absolute", top: 10, right: 10 }}>{closeBtn}</div>
              {photos.length > 1 && (
                <div style={{ position: "absolute", bottom: 10, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 5 }}>
                  {photos.map((_, i) => (
                    <button key={i} aria-label={`Photo ${i + 1}`} onClick={() => setImgIndex(i)}
                      style={{ width: i === imgIndex ? 16 : 6, height: 6, borderRadius: 3, border: "none", padding: 0, cursor: "pointer", background: i === imgIndex ? T.red : "rgba(255,255,255,.75)", transition: "all .2s" }} />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>{closeBtn}</div>
          )}

          <p style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".1em", color: T.red, textTransform: "uppercase", marginBottom: 4 }}>{shopName}</p>
          <div style={{ fontSize: 16, fontWeight: 800, color: T.black, marginBottom: 6 }}>{item.name}</div>
          {item.categoryId && (
            <p style={{ fontSize: 10.5, fontWeight: 700, color: T.mutedDim, marginBottom: 10, display: "flex", alignItems: "center", gap: 5 }}>
              <Tag size={10} /> {catPathLabel(item.categoryId)}
            </p>
          )}

          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            <span style={{ fontSize: 20, fontWeight: 900, color: T.black }}>{cur}{unitPrice.toFixed(2)}</span>
            {discount && <span style={{ fontSize: 13, color: T.mutedDim, textDecoration: "line-through" }}>{price(item.compareAtPrice ?? 0)}</span>}
            {measure && <span style={{ fontSize: 12, fontWeight: 800, color: T.muted }}>{measure}</span>}
            {hasRating && <span style={{ fontSize: 12, fontWeight: 700, color: T.muted }}>{summary.avgRating.toFixed(1)}★ ({summary.totalReviews.toLocaleString()})</span>}
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
            {item.isOrganic && chip("org", HEART.greenSoft, HEART.green, <Leaf size={11} />, "Organic")}
            {item.isVegan && chip("vg", HEART.greenSoft, HEART.green, <Leaf size={11} />, "Vegan")}
            {!item.isVegan && item.isVegetarian && chip("v", HEART.greenSoft, HEART.green, <Sparkles size={11} />, "Vegetarian")}
            {item.isGlutenFree && chip("gf", HEART.goldSoft, HEART.goldDark, <WheatOff size={11} />, "Gluten-free")}
            {(item.spiceLevel ?? 0) > 0 && chip("sp", HEART.redSoft, HEART.redDark, <Flame size={11} />, SPICE[item.spiceLevel] || "Spicy")}
            {fresh && chip("fr", `${fresh.color}1A`, fresh.color, <Clock size={11} />, fresh.text)}
            {storage && chip("st", HEART.goldSoft, HEART.goldDark, <StorageIcon size={11} />, storage.label)}
            {item.prepTimeMinutes ? chip("pt", HEART.bg, T.muted, <Clock size={11} />, `${item.prepTimeMinutes} min prep`) : null}
          </div>

          {/* ── SIZES ── */}
          {sizes.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", color: T.mutedDim, textTransform: "uppercase" }}>Size</span>
                {needsSize && <span style={{ fontSize: 10, fontWeight: 700, color: T.red }}>Please select</span>}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {sizes.map((s) => {
                  const on = selectedSize === s.id;
                  return (
                    <button key={s.id} className="fsb-opt" onClick={() => setSelectedSize(s.id)}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "11px 14px", borderRadius: 12, border: `1.5px solid ${on ? T.red : T.border}`, background: on ? T.redDim : "#fff", cursor: "pointer" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${on ? T.red : T.mutedDim}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {on && <span style={{ width: 9, height: 9, borderRadius: "50%", background: T.red }} />}
                        </span>
                        <span style={{ fontSize: 13.5, fontWeight: 700, color: T.black }}>{s.name}{s.unit ? ` · ${s.unit}` : ""}</span>
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: on ? T.red : T.muted }}>
                        {s.price > 0 ? `+${price(s.price)}` : "Included"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── COLOUR — only when the grower saved one ── */}
          {colorAttrs.map((c) => (
            <div key={c.id} style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", color: T.mutedDim, marginBottom: 10, textTransform: "uppercase" }}>{c.name}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {c.swatches.map((s) => (
                  <span key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, border: `1.5px solid ${T.border}`, borderRadius: 999, padding: "6px 13px 6px 7px", background: "#fff" }}>
                    <span style={{ width: 20, height: 20, borderRadius: "50%", background: s.hex || HEART.bg, border: `1px solid ${T.border}`, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: T.black }}>{s.name}</span>
                  </span>
                ))}
              </div>
            </div>
          ))}

          {/* ── EXTRAS ── */}
          {extras.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", color: T.mutedDim, marginBottom: 10, textTransform: "uppercase" }}>Add extras</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {extras.map((ex) => {
                  const on = selectedExtras.includes(ex.id);
                  return (
                    <button key={ex.id} className="fsb-opt"
                      onClick={() => setSelectedExtras((s) => (on ? s.filter((id) => id !== ex.id) : [...s, ex.id]))}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "11px 14px", borderRadius: 12, border: `1.5px solid ${on ? T.red : T.border}`, background: on ? T.redDim : "#fff", cursor: "pointer" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ width: 18, height: 18, borderRadius: 6, border: `2px solid ${on ? T.red : T.mutedDim}`, background: on ? T.red : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {on && <Check size={12} color="#fff" />}
                        </span>
                        <span style={{ fontSize: 13.5, fontWeight: 700, color: T.black }}>{ex.name}</span>
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: on ? T.red : T.muted }}>+{price(ex.price)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {item.description && (
            <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.65, marginBottom: 16 }}
              dangerouslySetInnerHTML={{ __html: item.description }} />
          )}

          {/* ── what the grower filled in ── */}
          {(() => {
            const rows: [string, string][] = detailRows.map((d) => [d.name, d.value]);
            if (item.calories) rows.push(["Calories", `${item.calories} kcal`]);
            if (item.harvestedOn) rows.push(["Harvested", new Date(item.harvestedOn).toLocaleDateString()]);
            if (item.bestBefore) rows.push(["Best before", new Date(item.bestBefore).toLocaleDateString()]);
            if (item.batchCode) rows.push(["Batch", item.batchCode]);
            if (allergens.length) rows.push(["Allergens", allergens.join(", ")]);
            if (!rows.length) return null;
            return (
              <div style={{ border: `1px solid ${T.border}`, borderRadius: 14, overflow: "hidden", marginBottom: 16 }}>
                {rows.map(([k, v], i) => (
                  <div key={`${k}-${i}`} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "9px 13px", background: i % 2 ? "#fff" : HEART.bg, fontSize: 12 }}>
                    <span style={{ color: T.mutedDim, fontWeight: 700 }}>{k}</span>
                    <span style={{ color: T.black, fontWeight: 700, textAlign: "right" }}>{v}</span>
                  </div>
                ))}
              </div>
            );
          })()}

          {soldOut ? (
            <p style={{ fontSize: 11.5, fontWeight: 800, color: T.mutedDim, marginBottom: 12 }}>Sold out for now</p>
          ) : stock <= 5 ? (
            <p style={{ fontSize: 11.5, fontWeight: 800, color: T.red, marginBottom: 12 }}>Only {stock} left</p>
          ) : null}

          {!soldOut && (
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.16em", color: T.mutedDim }}>QTY</span>
              <div style={{ display: "flex", border: `1.5px solid ${T.border}`, borderRadius: 12, overflow: "hidden", background: HEART.bg }}>
                <button aria-label="Decrease quantity" onClick={() => setQty((v) => Math.max(1, v - 1))}
                  style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: "pointer", color: T.red, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Minus size={14} />
                </button>
                <div style={{ width: 44, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, color: T.black, borderLeft: `1px solid ${T.border}`, borderRight: `1px solid ${T.border}` }}>{qty}</div>
                <button aria-label="Increase quantity" onClick={() => setQty((v) => Math.min(stock || 99, v + 1))} disabled={qty >= (stock || 99)}
                  style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: qty >= (stock || 99) ? "not-allowed" : "pointer", color: T.red, opacity: qty >= (stock || 99) ? 0.4 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Plus size={14} />
                </button>
              </div>
              {inCart > 0 && (
                <button onClick={() => removeLine(item, selectedSize, selectedExtras)}
                  style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", fontSize: 11.5, fontWeight: 800, color: T.red }}>
                  {inCart} in basket — remove one
                </button>
              )}
            </div>
          )}
        </div>

        <div style={{ padding: "14px 20px 20px", display: "flex", gap: 10, flexShrink: 0, borderTop: `1px solid ${T.border}` }}>
          <button disabled={!canAdd} onClick={() => { addLine(item, selectedSize, selectedExtras, qty); closeDetail(); }}
            style={{ flex: 1, padding: "13px 0", borderRadius: 12, border: `1.5px solid ${T.black}`, background: "transparent", color: T.black, fontWeight: 700, fontSize: 13, cursor: canAdd ? "pointer" : "not-allowed", opacity: canAdd ? 1 : 0.45 }}>
            {soldOut ? "Sold out" : needsSize ? "Pick a size" : "Add to basket"}
          </button>
          <button disabled={!canAdd} onClick={() => { addLine(item, selectedSize, selectedExtras, qty); closeDetail(); goToCheckout(); }}
            style={{ flex: 1.2, padding: "13px 0", borderRadius: 12, border: "none", background: canAdd ? GRAD_H : "#C8C8C8", color: "#fff", fontWeight: 800, fontSize: 13, cursor: canAdd ? "pointer" : "not-allowed" }}>
            Buy now · {lineTotal}
          </button>
        </div>
      </div>
    );

    return (
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeDetail}
          style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,.7)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", zIndex: 500 }}>
          {isMobile ? (
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
              onClick={(e) => e.stopPropagation()} style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 501 }}>
              {content}
            </motion.div>
          ) : (
            <div style={{ position: "fixed", inset: 0, zIndex: 501, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={(e) => e.stopPropagation()}>
              <motion.div initial={{ scale: 0.95, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 10 }}
                transition={{ type: "spring", damping: 26, stiffness: 340 }}
                style={{ width: 470, maxWidth: "100%", borderRadius: 20, overflow: "hidden", boxShadow: "0 32px 80px rgba(27,14,6,.35)" }}>
                {content}
              </motion.div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    );
  };

  const bottomTabs = () => (isMobile && userId ? (
    <div style={S.mobileTabs}>
      {[
        { icon: <Home size={22} />, label: "Home", active: false, action: () => navigate(`/FreshStoreHome/${userId}`) },
        { icon: <ShoppingBasket size={22} />, label: "Growers", active: true, action: () => navigate(`/FreshStores/${userId}`) },
        {
          label: "Basket", active: false, icon: (
            <div style={{ position: "relative" }}>
              <ShoppingCart size={22} />
              {cartTotal > 0 && (
                <span style={{ position: "absolute", top: -5, right: -5, background: T.red, color: "#fff", fontSize: 8, fontWeight: 900, width: 16, height: 16, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{cartTotal}</span>
              )}
            </div>
          ), action: goToCheckout,
        },
        { icon: <ShoppingBag size={22} />, label: "Orders", active: false, action: () => navigate(`/FreshOrders/${userId}`) },
        { icon: <User size={22} />, label: "Profile", active: false, action: () => navigate(`/profile/${userId}`) },
      ].map((t) => (
        <motion.div key={t.label} whileTap={{ scale: 0.88 } as any} onClick={t.action} className={`tab-item${t.active ? " active" : ""}`}>
          {React.cloneElement(t.icon as any, { color: t.active ? T.red : T.mutedDim })}
          <span>{t.label}</span>
        </motion.div>
      ))}
    </div>
  ) : null);

  /* ═══════════════════════════════════
     RENDER
  ═══════════════════════════════════ */
  return (
    <div style={S.page}>
      <style>{GLOBAL_CSS}</style>
      <PageWatermark src={shopLogo} />

      <div style={S.content}>
        <header style={S.headerTop}>{headerRow()}</header>

        {mobileCategorySheet()}

        {!isMobile && (
          <div style={{ ...S.desktopGrid, gridTemplateColumns: desktopCatOpen ? "300px 1fr" : "1fr" }}>
            {desktopCatOpen && <aside style={A.sidebar}>{deptTree()}</aside>}
            <main style={{ minWidth: 0 }}>
              {titleBanner()}
              {ovalBar()}
              {categoryIconBar()}
              {shopByCategory()}
              {renderProducts(false)}
            </main>
          </div>
        )}

        {isMobile && (
          <div style={{ padding: "14px 12px 0" }}>
            {titleBanner()}
            {ovalBar()}
            {categoryIconBar()}
            <div style={{ marginTop: 16 }}>{shopByCategory()}</div>
            {renderProducts(true)}
          </div>
        )}

        {detailModal()}
        {bottomTabs()}
      </div>
    </div>
  );
};

export default FreshStoreBrand;